<template>
  <el-dialog :title="editing ? '编辑图片' : '新增图片'" :visible="visible" width="560px" append-to-body custom-class="management-dialog image-resource-create-dialog"
             :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving"
             @update:visible="$emit('update:visible', $event)" @opened="focusName" @closed="reset">
    <el-form ref="form" :model="form" label-position="top" @submit.native.prevent="save">
      <p v-if="error" class="image-resource-form-error" role="alert" tabindex="-1" ref="error">{{ error }}</p>
      <el-form-item :label="editing ? '替换图片文件（可选）' : '图片文件'" :required="!editing">
        <div v-if="editing && !file" class="image-resource-selected-file">
          <img v-if="image.available" :src="image.url" :alt="image.name">
          <span>当前版本 v{{ image.version }}，未选择替换文件时继续使用当前图片。</span>
        </div>
        <div class="image-resource-upload" :class="{ 'is-dragging': dragDepth > 0 && !saving && !uncertain && !conflict }" :aria-busy="String(saving)" @dragenter.prevent="dragDepth++" @dragleave.prevent="dragDepth = Math.max(0, dragDepth - 1)" @dragover.prevent @drop.prevent="dropFile">
          <i class="el-icon-upload" aria-hidden="true"></i>
          <strong>{{ file ? '可拖放新图片替换所选文件' : '将图片拖放到此处' }}</strong>
          <p id="resource-upload-help">PNG / JPG / WebP / GIF，单文件不超过20 MB；暂不开放SVG上传。</p>
          <el-button size="small" type="primary" :disabled="saving || uncertain || conflict" @click="$refs.file.click()">{{ file ? '重新选择' : '选择图片' }}</el-button>
          <label for="resource-image-file" class="image-resource-file-label">选择图片文件</label>
          <input id="resource-image-file" ref="file" type="file" hidden accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
                 :disabled="saving || uncertain || conflict" aria-describedby="resource-upload-help" @change="selectFile($event.target.files[0])">
        </div>
        <div v-if="file" class="image-resource-selected-file">
          <img v-if="previewUrl" :src="previewUrl" alt="待上传图片预览">
          <span class="image-resource-file-info"><strong :title="file.name">{{ file.name }}</strong><small>{{ Math.max(1, Math.round(file.size / 1024)) }} KB · 保存时上传</small></span>
          <button type="button" :disabled="saving || uncertain || conflict" aria-label="移除所选图片" @click="removeFile"><i class="el-icon-close" aria-hidden="true"></i></button>
        </div>
      </el-form-item>
      <el-form-item label="图片名称" required :error="nameError">
        <el-input ref="name" v-model="form.name" maxlength="128" :disabled="saving || uncertain || conflict" placeholder="请输入图片名称" aria-label="图片名称"></el-input>
      </el-form-item>
      <el-form-item label="图片分类" required :error="catalogError">
        <el-select v-model="form.catalogId" filterable :disabled="saving || uncertain || conflict" placeholder="请选择图片分类" aria-label="图片分类" popper-class="image-resource-category-options">
          <el-option v-for="category in categories" :key="category.id" :value="category.id" :label="category.label"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-if="editing" label="图片说明">
        <el-input v-model="form.description" type="textarea" :rows="2" maxlength="1000" :disabled="saving || uncertain || conflict" placeholder="可选，说明图片用途" aria-label="图片说明"></el-input>
      </el-form-item>
      <p v-if="editing" class="image-resource-upload-note">保存后生成新版本，已保存的大屏继续使用原版本。</p>
      <el-form-item label="可见范围">
        <el-radio-group v-model="form.visibility" :disabled="saving || uncertain || conflict">
          <el-radio label="private">私有 · 按数据权限可见</el-radio>
          <el-radio label="public" :disabled="!canPublish">公开 · 其他账号可查询和使用</el-radio>
        </el-radio-group>
        <p v-if="!canPublish" class="image-resource-upload-note">公开图片需要图片资源的“公开”权限。</p>
      </el-form-item>
      <p class="image-resource-upload-note">新图片默认私有；此设置控制资源库可见范围，图片文件沿用平台公开链接。</p>
      <div v-if="saving" role="status" aria-live="polite" class="image-resource-save-status">
        <span class="image-resource-save-stage"><span class="ui-loading-spinner ui-loading-spinner--small" aria-hidden="true"></span>{{ stage === 'upload' ? '正在上传图片…' : '正在校验并保存图片资源…' }}</span>
        <el-progress v-if="stage === 'upload' && progress !== null" :percentage="progress"></el-progress>
      </div>
    </el-form>
    <span slot="footer">
      <el-button :disabled="saving || uncertain || conflict" @click="$emit('update:visible', false)">取消</el-button>
      <el-button v-if="uncertain || conflict" type="primary" @click="refreshAndClose">关闭并刷新列表</el-button>
      <el-button v-else type="primary" :loading="saving" :disabled="checking" @click="save">保存图片</el-button>
    </span>
  </el-dialog>
</template>

<script>
import store from '@/store'
import { uploadImageFile, createImage, updateImage } from '@/api/image-resources'
import { uploadedImageFile, createdImage, updatedImage, imageUploadError, imageError } from '@/utils/image-resources'

export default {
  props: { visible: Boolean, image: { type: Object, default: null }, categories: { type: Array, default: () => [] }, selectedCatalog: { type: String, default: '' } },
  data: () => ({
    form: { name: '', catalogId: '', description: '', visibility: 'private' }, file: null, previewUrl: '', uploaded: null, dragDepth: 0,
    checking: false, saving: false, stage: '', progress: null, error: '', nameError: '', catalogError: '', uncertain: false, conflict: false
  }),
  computed: {
    editing () { return !!this.image },
    canPublish () { return store.getters['auth/hasResourcePermission']('visualizationImage:publish') }
  },
  watch: {
    visible (value) { if (value) this.initialize() }
  },
  created () { if (this.visible) this.initialize() },
  beforeDestroy () { this.releasePreview() },
  methods: {
    initialize () {
      this.reset()
      this.form = this.editing ? { name: this.image.name, catalogId: this.image.catalogId, description: this.image.description || '', visibility: this.image.visibility || 'private' }
        : { name: '', catalogId: this.selectedCatalog, description: '', visibility: 'private' }
    },
    focusName () { if (this.$refs.name) this.$refs.name.focus() },
    releasePreview () { if (this.previewUrl) URL.revokeObjectURL(this.previewUrl); this.previewUrl = '' },
    removeFile () {
      if (this.saving || this.uncertain || this.conflict) return
      this.releasePreview(); this.file = null; this.uploaded = null; this.checking = false
      if (this.$refs.file) this.$refs.file.value = ''
    },
    reset () {
      this.dragDepth = 0
      this.uncertain = false; this.conflict = false; this.removeFile(); this.form = { name: '', catalogId: '', description: '' }
      this.error = ''; this.nameError = ''; this.catalogError = ''; this.stage = ''; this.progress = null
    },
    dropFile (event) {
      this.dragDepth = 0
      if (this.saving || this.uncertain || this.conflict) return
      if (event.dataTransfer.files.length !== 1) { this.error = '每次请选择一张图片'; return }
      this.selectFile(event.dataTransfer.files[0])
    },
    selectFile (file) {
      if (this.saving || this.uncertain || this.conflict || !file) return
      const error = imageUploadError(file)
      if (error) { this.error = error; if (this.$refs.file) this.$refs.file.value = ''; return }
      this.removeFile(); this.error = ''; this.file = file
      if (!this.form.name.trim()) this.form.name = file.name.replace(/\.[^.]+$/, '').slice(0, 128)
      this.previewUrl = URL.createObjectURL(file); this.checking = true
      const preview = new Image()
      preview.onload = () => { if (this.file === file && !this._isDestroyed) this.checking = false }
      preview.onerror = () => {
        if (this.file !== file || this._isDestroyed) return
        this.removeFile(); this.error = '无法读取所选图片，请更换有效图片文件'
      }
      preview.src = this.previewUrl
    },
    async save () {
      if (this.saving || this.checking || this.uncertain || this.conflict) return
      this.nameError = this.form.name.trim() ? '' : '请输入图片名称'
      this.catalogError = this.categories.some(item => item.id === this.form.catalogId) ? '' : '请选择有效图片分类'
      this.error = this.editing && !this.file ? '' : imageUploadError(this.file)
      if (this.editing && (!this.image.editable || !Number.isSafeInteger(this.image.revision) || this.image.revision < 1)) this.error = '图片编辑信息无效，请关闭并刷新后重试'
      if (this.form.visibility === 'public' && !this.canPublish) this.error = '当前账号没有公开图片权限，请选择私有或联系管理员授权'
      if (this.error || this.nameError || this.catalogError) return
      const request = { name: this.form.name.trim(), catalogId: this.form.catalogId, visibility: this.form.visibility || 'private' }
      const revision = this.editing ? this.image.revision : null
      const id = this.editing ? this.image.id : null
      if (this.editing) Object.assign(request, { revision, description: this.form.description.trim() })
      this.saving = true; this.error = ''; this.progress = null
      try {
        if (this.file && !this.uploaded) {
          this.stage = 'upload'
          // 沿用背景图上传的兼容处理，旧 Mock.js 的 upload 不支持 progress 监听。
          const upload = new window.XMLHttpRequest().upload
          const options = upload && typeof upload.addEventListener === 'function' ? { onUploadProgress: event => {
            if (!this._isDestroyed && event.total) this.progress = Math.min(100, Math.round(event.loaded / event.total * 100))
          } } : {}
          this.uploaded = uploadedImageFile(await uploadImageFile(this.file, options))
        }
        if (this._isDestroyed) return
        if (request.visibility === 'public' && !this.canPublish) { this.error = '公开权限已变更，本次未提交'; return }
        this.stage = 'register'
        if (this.uploaded) request.fileId = this.uploaded.id
        const response = this.editing ? await updateImage(id, request) : await createImage(request)
        // 服务端若已写入但返回格式异常，不允许再次提交相同保存。
        this.uncertain = true
        const image = this.editing ? updatedImage(response, id, revision) : createdImage(response)
        if (this._isDestroyed) return
        this.uncertain = false; this.$emit(this.editing ? 'updated' : 'created', image); this.$emit('update:visible', false)
      } catch (error) {
        if (this._isDestroyed) return
        if (this.stage === 'register' && (!error.status || error.status >= 500)) this.uncertain = true
        this.conflict = this.editing && error.status === 409
        this.error = this.conflict ? '图片已被修改，请关闭并刷新列表后重新编辑'
          : this.uncertain ? '保存结果未确认，请关闭并刷新列表检查，勿重复提交' : imageError(error)
        this.$nextTick(() => { if (this.$refs.error) this.$refs.error.focus() })
      } finally { if (!this._isDestroyed) this.saving = false }
    },
    refreshAndClose () { this.$emit('refresh', { catalogId: this.form.catalogId, editing: this.editing }); this.$emit('update:visible', false) }
  }
}
</script>

<style lang="scss">
.image-resource-create-dialog, .image-resource-catalog-dialog {
  width: min(560px, calc(100% - 32px)) !important; margin-top: 6vh !important; max-height: 88vh; max-height: 88dvh; display: flex; flex-direction: column; border-radius: 6px; background: #fff; color: #1f2937;
  &, * { scrollbar-width: none; -ms-overflow-style: none; }
  *::-webkit-scrollbar { width: 0; height: 0; display: none; }
  button:focus-visible, input:focus-visible { outline: 2px solid #1677ff; outline-offset: 2px; }
  .el-dialog__body { padding: 12px 24px; min-height: 0; overflow: auto; }
  .el-dialog__footer { padding-bottom: max(20px, env(safe-area-inset-bottom)); }
  .el-dialog__title { color: #1f2937 !important; font-size: 16px; }
  .el-form-item { margin-bottom: 18px; }
  .el-form-item__label { color: #1f2937 !important; padding-left: 0; }
  .el-form-item__content { padding-right: 0; }
  .el-button { border-radius: 4px; }
  .el-button--primary { color: #fff; background: #1677ff; border-color: #1677ff; }
  .el-button--default { color: #1f2937; background: #fff; border-color: #dcdfe6; }
  .el-select { width: 100%; }
  input, textarea { color: #1f2937 !important; background: #fff !important; border-color: #dcdfe6 !important; }
}
.image-resource-form-error { padding: 10px 12px; margin: 0 0 16px; color: #b42318; background: #fef3f2; border: 1px solid #fecdca; border-radius: 4px; line-height: 1.7; overflow-wrap: anywhere; }
.image-resource-catalog-dialog { width: min(480px, calc(100% - 32px)) !important; }
.image-resource-upload { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 20px 14px; border: 1px dashed #c0c4cc; border-radius: 5px; background: #fafbfc; color: #667085; text-align: center; line-height: 1.7; }
.image-resource-upload.is-dragging, .image-resource-upload:focus-within { border-color: #1677ff; background: #f1f6ff; }
.image-resource-upload .el-button { margin-top: 8px; min-width: 104px; }
.image-resource-upload > i { font-size: 34px; color: #98a2b3; }
.image-resource-upload strong { font-weight: 500; color: #1f2937; }
.image-resource-upload p { margin: 0; font-size: 12px; }
.image-resource-upload input { max-width: 100%; }
.image-resource-file-label { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); }
.image-resource-selected-file { display: flex; align-items: center; gap: 10px; padding-top: 10px; }
.image-resource-selected-file img { width: 60px; height: 48px; object-fit: contain; background: #f4f5f7; }
.image-resource-selected-file span { flex: 1; min-width: 0; overflow-wrap: anywhere; line-height: 1.6; }
.image-resource-file-info strong { display: block; color: #344054; font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.image-resource-file-info small { display: block; margin-top: 4px; color: #64748b; font-size: 12px; }
.image-resource-save-stage { display: flex; align-items: center; gap: 8px; }
.image-resource-selected-file button { flex: 0 0 32px; width: 32px; height: 32px; border: 0; border-radius: 4px; background: #f4f5f7; color: #667085; cursor: pointer; }
.image-resource-upload-note, .image-resource-save-status { margin: 8px 0; font-size: 12px; line-height: 1.7; color: #667085; }
.image-resource-category-options.el-select-dropdown { background: #fff; border: 1px solid #e4e7ed; }
.image-resource-category-options .el-select-dropdown__item { color: #1f2937; }
.image-resource-category-options .el-select-dropdown__item.hover, .image-resource-category-options .el-select-dropdown__item.selected { color: #1677ff; background: #edf4ff; }
@media (max-width: 700px), (pointer: coarse) {
  .image-resource-create-dialog .el-button, .image-resource-catalog-dialog .el-button { min-height: 44px; }
  .image-resource-selected-file button { width: 44px; height: 44px; flex-basis: 44px; }
}
</style>
